import { Dispatch, SetStateAction, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { preview_image } from "@/constants";
import { PictureUploader, SelectBox } from "@/components";
import HierarchicalCheckbox from "@/components/checkbox/hierarchical";
import { PAGE_OPTIONS, expandForDisplay, normalizeForStorage, type PageOption } from "@/roles/protection/pageOptions";
import { useGetTenantAllowedPagesQuery, useSetTenantAllowedPagesMutation } from "@/graphql/page-access";
import { tenantManagementApi, useCreateTenantMutation, useUpdateCompanyInfoMutation } from "@/services";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useSnackbar, useUploadFile } from "@/hooks";
import { StyledTextInput } from "./styles";
import { ValidationPatterns } from "@/utils/form-validation";
import { useAppDispatch } from "@/store";
import { tokens } from "@/tokens";

/* ── Shared card styles ────────────────────────────────────────────── */
const cardSx = {
  border: `1px solid ${tokens.border.default}`,
  borderRadius: tokens.radius.lg,
  overflow: "hidden",
} as const;

const cardHeaderSx = {
  px: 2,
  py: 1.25,
  bgcolor: tokens.bg.hover,
  borderBottom: `1px solid ${tokens.border.default}`,
} as const;

const cardBodySx = { p: 2 } as const;

const sectionTitleSx = {
  fontSize: tokens.fontSize.sm2,
  fontWeight: tokens.fontWeight.bold,
  color: tokens.text.primary,
} as const;

/* ── Component ─────────────────────────────────────────────────────── */
type AddTenantModalProps = {
  show: boolean;
  setShow: Dispatch<SetStateAction<boolean>>;
  selectedTenant: ITenant | null;
};

const AddTenantModal = ({
  show,
  setShow,
  selectedTenant,
}: AddTenantModalProps) => {
  const { control, handleSubmit, reset, setValue, watch } = useForm<TenantFormValues>({
    defaultValues: {
      firstName: "",
      email: "",
      companyName: "",
      companyEmail: "",
      companyLogo: null,
      businessAddress: "",
      tenantType: "SERVICE_PROVIDER",
    },
  });
  const dispatch = useAppDispatch();

  const [profilePreview, setProfilePreview] = useState<string | undefined>(
    undefined
  );
  const { isLoading: profileImgLoading, uploadFile } = useUploadFile();

  const { show: showSnackBar } = useSnackbar();

  const [addTenant, { isLoading }] = useCreateTenantMutation();
  const [updateCompanyInfo, { isLoading: isUpdateLoading }] = useUpdateCompanyInfoMutation();
  const [allowedPages, setAllowedPages] = useState<string[]>([]);
  const { data: tenantPages } = useGetTenantAllowedPagesQuery(
    { tenantId: selectedTenant?._id },
    { skip: !selectedTenant?._id }
  );
  const [setTenantAllowedPages] = useSetTenantAllowedPagesMutation();
  const tenantTypeValue = watch("tenantType");

  const isEditing = !!selectedTenant?._id;
  const busy = isLoading || profileImgLoading || isUpdateLoading;

  const handleProfileChange = (file: File | null) => {
    if (file) {
      const fileURL = URL.createObjectURL(file);
      setValue("companyLogo", file);
      setProfilePreview(fileURL);
    }
  };

  const handleCreateTenant: SubmitHandler<TenantFormValues> = async (data) => {
    const {
      firstName,
      email,
      companyName,
      companyEmail,
      companyLogo,
      businessAddress,
      tenantType,
    } = data;

    let companyLogoKey;
    if (companyLogo) {
      const data = await uploadFile(companyLogo as File);
      companyLogoKey = data?.key || "";
    }
    const tenantData = {
      firstName,
      email,
      companyName,
      companyEmail,
      companyLogo: companyLogoKey || "",
      businessAddress,
      tenantType,
    };

    const finalPages = normalizeForStorage(allowedPages, tenantPageOptions);
    try {
      if (isEditing) {
        await updateCompanyInfo({
          tenantData: {
            businessAddress,
            companyName,
            companyLogo: companyLogoKey,
          },
          tenantId: selectedTenant?._id as string,
        }).unwrap();
        if (finalPages.length) {
          await setTenantAllowedPages({ tenantId: selectedTenant!._id as string, pages: finalPages }).unwrap();
        }
        dispatch(tenantManagementApi.util.invalidateTags([{ type: "tenantManagement" }]));
      } else {
        await addTenant({
          tenantData: { ...tenantData, allowedPages: finalPages },
        }).unwrap();
      }
      showSnackBar(
        isEditing ? "Tenant Updated Successfully!" : "Client Created Successfully!",
        "",
        "success"
      );
      setShow(false);
      reset();
    } catch (error: any) {
      showSnackBar(error.message, "", "error");
    }
  };

  useEffect(() => {
    if (selectedTenant?._id) {
      setProfilePreview(selectedTenant?.companyLogo || "");
      setValue("companyName", selectedTenant?.companyName);
      setValue("companyEmail", selectedTenant?.companyEmail);
      setValue("businessAddress", selectedTenant?.businessAddress || "");
    }
  }, [selectedTenant?._id]);

  useEffect(() => {
    if (tenantPages?.pages) setAllowedPages(expandForDisplay(tenantPages.pages, tenantPageOptions));
  }, [tenantPages]);

  const isSP = isEditing ? selectedTenant?.tenantType === "SERVICE_PROVIDER" : tenantTypeValue === "SERVICE_PROVIDER";
  const tenantPageOptions: PageOption[] = [
    ...PAGE_OPTIONS,
    ...(isSP ? [{ key: "PRODUCT_HUB_DUTY_COLUMNS", label: "Duty Columns" }] : []),
  ];

  return (
    <Dialog
      open={show}
      onClose={() => setShow(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: tokens.radius.lg,
          overflow: "hidden",
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* ── Header ──────────────────────────────────────────────── */}
      <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: `1px solid ${tokens.border.default}`, flexShrink: 0 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography sx={{ fontSize: tokens.fontSize.xl, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, lineHeight: 1.3 }}>
              {isEditing ? "Edit Tenant" : "Add New Tenant"}
            </Typography>
            <Typography sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.muted, mt: 0.5 }}>
              {isEditing
                ? "Update tenant details and page access"
                : "They'll be able to access Riffs and see their data once invited"}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() => setShow(false)}
            sx={{ color: tokens.text.muted, "&:hover": { color: tokens.text.primary } }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Stack>
      </Box>

      {/* ── Scrollable Body ─────────────────────────────────────── */}
      <Box
        component="form"
        onSubmit={handleSubmit(handleCreateTenant)}
        sx={{ flex: 1, overflow: "auto", px: 3, py: 2.5 }}
      >
        <Stack spacing={2.5}>
          {/* User Details — new tenant only */}
          {!isEditing && (
            <Box sx={cardSx}>
              <Box sx={cardHeaderSx}>
                <Typography sx={sectionTitleSx}>User Details</Typography>
              </Box>
              <Box sx={cardBodySx}>
                <Controller
                  name="firstName"
                  control={control}
                  rules={{ required: "Name is required" }}
                  render={({ field: { onChange, ...rest }, fieldState }) => (
                    <StyledTextInput
                      label="First Name"
                      type="text"
                      {...rest}
                      placeholder="Name"
                      handleChangeValue={onChange}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
                <Controller
                  name="email"
                  control={control}
                  rules={{
                    required: "Email is required",
                    pattern: {
                      value: ValidationPatterns.email,
                      message: "Invalid email address",
                    },
                  }}
                  render={({ field: { onChange, ...rest }, fieldState }) => (
                    <StyledTextInput
                      label="Email Address"
                      {...rest}
                      type="text"
                      placeholder="h1@example.com"
                      handleChangeValue={onChange}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
                <Controller
                  name="tenantType"
                  control={control}
                  rules={{ required: "Tenant type is required" }}
                  render={({
                    field: { onChange, value, ...rest },
                    fieldState,
                  }) => (
                    <SelectBox
                      label="Tenant Type"
                      placeholder="Select tenant type"
                      value={value}
                      handleChangeValue={onChange}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      options={[
                        { value: "SERVICE_PROVIDER", label: "Service Provider" },
                        { value: "TRADEDIVE_CLIENT", label: "Riffs Client" },
                      ]}
                      {...rest}
                    />
                  )}
                />
              </Box>
            </Box>
          )}

          {/* Company Details */}
          <Box sx={cardSx}>
            <Box sx={cardHeaderSx}>
              <Typography sx={sectionTitleSx}>Company Details</Typography>
            </Box>
            <Box sx={cardBodySx}>
              <PictureUploader
                title="Company Logo"
                description="We recommend an image of at least 400x400"
                imgUrl={profilePreview ? profilePreview : preview_image}
                customPreviewType={profilePreview ? "rounded" : "default"}
                onChange={handleProfileChange}
              />
              <Box sx={{ mt: 2.5 }}>
                <Controller
                  name="companyName"
                  control={control}
                  rules={{ required: "Company name is required" }}
                  render={({ field: { onChange, ...rest }, fieldState }) => (
                    <StyledTextInput
                      label="Company Name"
                      type="text"
                      {...rest}
                      placeholder="Company Name"
                      handleChangeValue={onChange}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
                <Controller
                  name="companyEmail"
                  control={control}
                  rules={{
                    required: "Company email is required",
                    pattern: {
                      value: ValidationPatterns.email,
                      message: "Invalid email address",
                    },
                  }}
                  render={({ field: { onChange, ...rest }, fieldState }) => (
                    <StyledTextInput
                      label="Company Email Address"
                      {...rest}
                      disabled={isEditing}
                      type="text"
                      placeholder="company@example.com"
                      handleChangeValue={onChange}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
                <Controller
                  name="businessAddress"
                  control={control}
                  render={({ field: { onChange, ...rest } }) => (
                    <StyledTextInput
                      label="Company Address"
                      {...rest}
                      type="text"
                      placeholder="Company Address"
                      handleChangeValue={onChange}
                    />
                  )}
                />
              </Box>
            </Box>
          </Box>

          {/* Page Access */}
          <Box sx={cardSx}>
            <Box sx={cardHeaderSx}>
              <Typography sx={sectionTitleSx}>Page Access</Typography>
            </Box>
            <Box sx={cardBodySx}>
              <Typography sx={{ color: tokens.text.muted, fontSize: tokens.fontSize.xs, mb: 1.5 }}>
                Select which app pages are enabled for this tenant by default
              </Typography>
              <HierarchicalCheckbox
                options={tenantPageOptions}
                selectedKeys={allowedPages}
                onChange={setAllowedPages}
              />
            </Box>
          </Box>
        </Stack>
      </Box>

      {/* ── Footer ──────────────────────────────────────────────── */}
      <Box sx={{
        px: 3,
        py: 2,
        borderTop: `1px solid ${tokens.border.default}`,
        display: "flex",
        justifyContent: "flex-end",
        gap: 1.5,
        flexShrink: 0,
      }}>
        <Button
          variant="outlined"
          onClick={() => setShow(false)}
          disabled={busy}
          sx={{
            borderColor: tokens.border.default,
            color: tokens.text.secondary,
            fontWeight: tokens.fontWeight.semibold,
            fontSize: tokens.fontSize.sm2,
            borderRadius: tokens.radius.md,
            textTransform: "none",
            px: 3,
            "&:hover": { borderColor: tokens.text.muted, bgcolor: tokens.bg.hover },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit(handleCreateTenant)}
          disabled={busy}
          sx={{
            fontWeight: tokens.fontWeight.semibold,
            fontSize: tokens.fontSize.sm2,
            borderRadius: tokens.radius.md,
            textTransform: "none",
            px: 3,
            bgcolor: tokens.brand.primary,
            "&:hover": { bgcolor: tokens.brand.primaryHover },
          }}
        >
          {busy && <CircularProgress size={16} sx={{ color: "white", mr: 1 }} />}
          {isEditing ? "Update Tenant" : "Create Tenant"}
        </Button>
      </Box>
    </Dialog>
  );
};

export default AddTenantModal;
